import React, { useState } from 'react';
import { Modal, View, StyleSheet, Pressable, ScrollView, SafeAreaView, Platform, Text, useWindowDimensions } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import { colors, typography } from '../../styles/theme';

export const modalStyles = StyleSheet.create({
  headerText: {
    ...typography.header,
    textAlign: 'center',
    marginBottom: 16,
  },
  subtitleText: {
    ...typography.body,
    color: colors.textMuted,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  primaryButton: {
    backgroundColor: colors.primary,
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 5,
    borderWidth: 0,
    width: '100%',
    alignItems: 'center',
    boxShadow: `2px 2px 0px ${colors.shadow}`,
    elevation: 2,
  },
  primaryButtonHover: {
    backgroundColor: '#9e3f1b', // Slightly darker than #ba4b21
  },
  primaryButtonText: {
    ...typography.bodyBold,
    color: '#ffffff',
    letterSpacing: 0.3,
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.text,
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 5,
    width: '100%',
    alignItems: 'center',
    boxShadow: `2px 2px 0px ${colors.shadow}`,
    elevation: 2,
  },
  secondaryButtonText: {
    ...typography.bodyBold,
    color: colors.text,
    letterSpacing: 0.3,
  },
  clearButton: {
    backgroundColor: 'transparent',
    paddingVertical: 16,
    paddingHorizontal: 16,
    width: '100%',
    alignItems: 'center',
    borderRadius: 5,
  },
  clearButtonText: {
    ...typography.bodyBold,
    color: colors.text,
    textAlign: 'center',
  },
  linkButtonText: {
    ...typography.body,
    color: colors.textMuted,
    textAlign: 'center',
    paddingVertical: 12,
    fontWeight: 'bold',
  },
  closeText: {
    ...typography.body,
    color: colors.text,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 8,
  },
  disabledButton: {
    backgroundColor: '#cccccc',
    boxShadow: `1px 1px 0px ${colors.shadow}`,
    elevation: 1,
  },
  disabledButtonText: {
    color: '#888888',
  },
  buttonContainer: { gap: 12, width: '100%' },
});

const styles = StyleSheet.create({
  overlay: {

    flex: 1,
    backgroundColor: colors.overlay,
  },
  safeArea: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
  },
  container: {
    width: '90%',
    maxWidth: 600,
    maxHeight: '100%',
    backgroundColor: colors.background,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    boxShadow: `3px 3px 0px ${colors.shadow}`,
    elevation: 3,
    flexShrink: 0, // Prevent collapse
  },
  scrollContent: {
    padding: 16,
    paddingTop: 16,
    alignItems: 'stretch',
    flexGrow: 1, // Ensure content pushes open
  },
  scrollContentWithClose: {
    paddingTop: 32,
  },
  scrollContentWithHeader: {
    paddingTop: 0,
  },
  headerContainer: {
    paddingTop: 16,
    paddingHorizontal: 16,
    marginHorizontal: -16,
    zIndex: 1,
    backgroundColor: colors.background,
    paddingBottom: 8,
  },
  headerContainerWithClose: {
    paddingTop: 32,
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
    padding: 8,
    borderRadius: 5,
  },
  buttonHover: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
});

interface BaseModalProps {
  visible: boolean;
  onClose?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  animationType?: 'none' | 'slide' | 'fade';
  isFullScreenOnMobile?: boolean;
}

export default function BaseModal({ visible, onClose, header, footer, children, animationType = 'fade', isFullScreenOnMobile = true }: BaseModalProps) {
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const closeColor = isCloseHovered ? '#8992a5' : '#999999';
  const { width, height } = useWindowDimensions();
  const isMobile = width <= 600;
  const modalMaxHeight = isMobile && isFullScreenOnMobile ? '100%' : (height - 64);

  return (
    <Modal visible={visible} transparent={true} animationType={animationType} onRequestClose={onClose}>
      <Pressable
        style={[
          styles.overlay,
          isMobile && isFullScreenOnMobile && { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 },
          Platform.OS === 'web' && { cursor: 'default' } as any
        ]}
        onPress={onClose}
        disabled={!onClose}
      >
        <SafeAreaView style={[
          styles.safeArea,
          isMobile && isFullScreenOnMobile && { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 },
        ]}>
          <Pressable
            style={[
              styles.container,
              { maxHeight: modalMaxHeight },
              isMobile && isFullScreenOnMobile && { width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0, borderWidth: 0 },
              Platform.OS === 'web' && { cursor: 'default' } as any
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            {onClose && (
              <Pressable
                onPress={onClose}
                onHoverIn={Platform.OS === 'web' ? () => setIsCloseHovered(true) : undefined}
                onHoverOut={Platform.OS === 'web' ? () => setIsCloseHovered(false) : undefined}
                style={[styles.closeButton, isCloseHovered && styles.buttonHover]}
                accessibilityLabel="Close modal"
              >
                <Svg width={18} height={18} viewBox="0 0 24 24">
                  <Line x1="18" y1="6" x2="6" y2="18" stroke={closeColor} strokeWidth={3} strokeLinecap="round" />
                  <Line x1="6" y1="6" x2="18" y2="18" stroke={closeColor} strokeWidth={3} strokeLinecap="round" />
                </Svg>
              </Pressable>
            )}
            <ScrollView
              showsVerticalScrollIndicator={false}
              style={styles.scrollView}
              contentContainerStyle={[
                styles.scrollContent,
                (onClose && !header) ? styles.scrollContentWithClose : null,
                header ? styles.scrollContentWithHeader : null
              ]}
              stickyHeaderIndices={header ? [0] : undefined}
            >
              {header && (
                <View style={[styles.headerContainer, onClose && styles.headerContainerWithClose]}>
                  {header}
                </View>
              )}
              {children}
            </ScrollView>
            {footer}
          </Pressable>
        </SafeAreaView>
      </Pressable>
    </Modal>
  );
}

interface HoverButtonProps {
  onPress: () => void;
  children: React.ReactNode;
  style?: any;
  textStyle?: any;
  hoverStyle?: any;
  disabled?: boolean;
}

export const HoverButton = ({ onPress, children, style, textStyle, hoverStyle, disabled }: HoverButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      onHoverIn={Platform.OS === 'web' && !disabled ? () => setIsHovered(true) : undefined}
      onHoverOut={Platform.OS === 'web' && !disabled ? () => setIsHovered(false) : undefined}
      disabled={disabled}
      style={[
        style,
        isHovered && !disabled && (hoverStyle || styles.buttonHover),
        disabled && modalStyles.disabledButton
      ]}
    >
      <Text style={[textStyle, disabled && modalStyles.disabledButtonText]}>{children}</Text>
    </Pressable>
  );
};
